import { computed, inject, Injectable, type Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, map } from 'rxjs';

import type { BlockContext } from '@front-platform/content-builder';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';
import { CartStoreSelector } from '@common/selectors/cart.selector';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { ShopStoreSelector } from '@common/selectors/shop.selector';
import { ComoService } from '@core/services/como/como.service';
import { PongoService } from '@core/services/pongo/pongo.service';
import { ZerosixService } from '@core/services/zerosix/zerosix.service';
import { JourneyParametersRuntimeService } from '@core/services/content-builder/journey-parameters-runtime.service';
import { DateUtils } from '@shared/utils/date/date.utils';
import { DoodAppScreen } from '@store/order/order.model';

function mapDeviceToBlockContext(screen: DoodAppScreen, isKiosk: boolean): BlockContext['device'] {
  if (isKiosk) {
    return 'kiosk';
  }
  if (screen === DoodAppScreen.desktop) {
    return 'desktop';
  }
  return 'mobile';
}

@Injectable({ providedIn: 'root' })
export class BlockConfigContextService {
  private readonly authStoreSelector = inject(AuthStoreSelector);
  private readonly cartStoreSelector = inject(CartStoreSelector);
  private readonly settingsStoreSelector = inject(SettingsStoreSelector);
  private readonly shopStoreSelector = inject(ShopStoreSelector);
  private readonly comoService = inject(ComoService);
  private readonly zerosixService = inject(ZerosixService);
  private readonly pongoService = inject(PongoService);
  private readonly journeyParametersRuntime = inject(JourneyParametersRuntimeService);

  private readonly locale = toSignal(this.settingsStoreSelector.selectAppLocale, {
    initialValue: 'fr',
  });

  private readonly userIsLoggedIn = toSignal(this.authStoreSelector.selectUserIsLoggedIn, {
    initialValue: false,
  });

  private readonly isLoyaltyMember = toSignal(
    combineLatest({
      isComoMember: this.comoService.isUserComoAccountEnabledOnMarketplaceLevel$(),
      isZerosixMember: this.zerosixService.isUserZeroSixCardEnabledOnMarketplaceLevel$(),
      isPongoMember: this.pongoService.isUserPongoCustomerEnabledOnMarketplaceLevel$(),
    }).pipe(
      map(
        ({ isComoMember, isZerosixMember, isPongoMember }) =>
          isComoMember || isZerosixMember || isPongoMember,
      ),
    ),
    { initialValue: false },
  );

  readonly context: Signal<BlockContext> = computed(() => {
    const now = DateUtils.dayjsInMarketplaceTimezone();
    const user = this.authStoreSelector.selectUser();
    const activeCart = this.cartStoreSelector.selectActive();
    const activeShop = this.shopStoreSelector.selectActive();

    return {
      locale: this.locale(),
      device: mapDeviceToBlockContext(
        this.settingsStoreSelector.selectDeviceScreenSignal(),
        Boolean(this.settingsStoreSelector.deviceIsKiosk()),
      ),
      userAuth: this.userIsLoggedIn() ? 'authenticated' : 'anonymous',
      timeOfDay: { now: now.format('HH:mm') },
      weekday: { now: now.toDate() },
      dateRange: { now: now.toDate() },
      journeyParameters: this.journeyParametersRuntime.params(),
      cartType: activeCart?.type,
      shop: activeShop ? (activeShop as unknown as Record<string, unknown>) : undefined,
      userField: user ? (user as unknown as Record<string, unknown>) : undefined,
      isLoyaltyMember: this.isLoyaltyMember(),
      userTags: Array.isArray(user?.tags)
        ? user.tags
            .map(tag => tag?.label)
            .filter((label): label is string => typeof label === 'string' && label.length > 0)
        : [],
    };
  });
}
